<script setup lang="ts">
  import type { IGenericAPIResponse } from '#api';
  import { LazyModalGeneric } from '#components';
  import { rules } from '#shared/validation';
  import * as z from 'zod';

  interface IContactUsForm {
    fullName: string;
    email: string;
    subject: string;
    attachment: File | null;
    message: string;
  }

  definePageMeta({
    availableForGuests: true,
    hideRightBar: true,
  });

  const { windowWidth } = useDeviceType();
  const { $authApi } = useTypedNuxtApp();

  const SUBJECTS = ['Payments', 'Games', 'Website', 'Account', 'Rewards', 'Other'];

  const isPending = ref(false);
  const form = ref<IContactUsForm>({
    fullName: '',
    email: '',
    subject: '',
    attachment: null,
    message: '',
  });
  const { email, message, fullName, required } = rules;
  const schema = z.object({
    email,
    message,
    fullName,
    subject: required,
    attachment: z
      .any()
      .refine((file) => {
        if (!file) return true;
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
        return allowedTypes.includes(file.type);
      }, 'Only .jpg, .jpeg, and .png files are allowed.')
      .refine((file) => {
        if (!file) return true;
        return file.size <= 5 * 1024 * 1024;
      }, 'File size must be less than 5MB'),
  });
  const isValidForm = computed(() => schema.safeParse(form.value).success);
  const attachmentErrors = computed(() => {
    const res = schema.safeParse(form.value);

    if (res.success) return [];

    try {
      const parsed = JSON.parse(res.error.message);
      const errorList = parsed.filter((error: { path: [string] }) => error.path.includes('attachment'));

      if (errorList.length > 0) {
        return errorList.map((error: { message: string }) => ({
          message: error.message,
          key: `${error.message.split(' ').join('-')}-${Date.now()}`,
        }));
      }
      return errorList;
    } catch {
      return [];
    }
  });

  async function submit() {
    try {
      isPending.value = true;
      const { fullName, email, subject, message: yourMessage } = form.value;
      const [firstName, lastName] = fullName.split(' ');
      const { status, message } = await $authApi.post<IGenericAPIResponse>('/sendContactFormEmail', {
        firstName,
        lastName,
        email,
        subject,
        yourMessage,
      });

      await stores.modals.open('form-submission-result', LazyModalGeneric, {
        title: status === 'SUCCESS' ? 'We have received your query' : 'Error',
        text: status === 'SUCCESS' ? 'and will aim to respond within 48 hours' : message,
      });

      isPending.value = false;
    } catch (e) {
      isPending.value = false;
      console.error(e);
    }
  }

  useSchemaOrg([
    defineWebPage({
      '@type': 'ContactPage',
      name: 'Contact Us',
    }),
  ]);
</script>

<template>
  <div class="flex items-center mx-auto max-w-[916px] w-full">
    <div class="w-full max-w-[472px] mx-auto flex flex-col gap-4 my-[40px] relative z-10">
      <h1 class="text-white font-barlow text-5xl not-italic font-extrabold leading-[120%] uppercase">Contact Us</h1>
      <p class="text-white font-barlow text-base not-italic font-normal leading-[150%]">
        Should you have any questions or experience any issues, please let us know. We kindly request that you provide
        details so we can offer the most appropriate and accurate responses.
      </p>

      <UForm :state="form" :schema="schema" class="flex flex-col gap-4" @submit="submit">
        <CoreFormField label="Full Name" name="fullName">
          <CoreInput v-model="form.fullName" placeholder="Please enter your full name" />
        </CoreFormField>
        <CoreFormField label="Email" required name="email">
          <CoreInput v-model="form.email" placeholder="Please enter your e-mail" type="email" />
        </CoreFormField>
        <CoreFormField name="subject" label="Subject Related to" required>
          <CoreSelect v-model="form.subject" placeholder="Choose a subject" :items="SUBJECTS" />
        </CoreFormField>
        <CoreFormField name="attachments">
          <CoreFileUploader v-model="form.attachment" is-short label="Add attachment" accept=".jpg, .jpeg, .png" />
          <template v-if="attachmentErrors.length > 0">
            <p v-for="error in attachmentErrors" :key="error.key" class="text-red-500">
              {{ error.message }}
            </p>
          </template>
        </CoreFormField>
        <CoreFormField class="mt-2" name="message" label="Your Message" required>
          <CoreTextarea v-model="form.message" />
        </CoreFormField>

        <CoreButton :disabled="!isValidForm || isPending" type="submit">Send Message</CoreButton>
      </UForm>
    </div>
    <template v-if="windowWidth >= 1600">
      <NuxtImg
        src="images/contact-us/astronaut.webp"
        class="relative z-10 -rotate-[1deg] h-[515px]"
        alt="contact-us"
        decoding="auto"
        :ismap="false"
        preload
      />
      <CoreImage
        src="images/contact-us/background.webp"
        class="fixed z-0 top-0 left-0 pointer-events-none w-full h-full object-cover"
        alt="contact-us"
        decoding="auto"
        :ismap="false"
        preload
      />
    </template>
  </div>
</template>
